import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    fetchGalleri,
    GalleriApiError,
    lagSentraltGalleriOppslag,
    sentraleElementer,
    type GalleriElement,
    type SentraltGalleriTilstand,
} from './galleri';

export const SENTRALT_GALLERI_QUERY_KEY = ['dibkGalleri'] as const;

/**
 * Hele galleriet fra DiBK, delt cache på tvers av komponenter. Hentes ved
 * sideinnlasting og på nytt når dataene er eldre enn 10 min og fanen får fokus,
 * så endringer hos DiBK slår gjennom uten egen synk.
 */
function useDibkGalleri<T>(enabled: boolean, select?: (items: GalleriElement[]) => T) {
    return useQuery({
        queryKey: SENTRALT_GALLERI_QUERY_KEY,
        queryFn: ({ signal }) => fetchGalleri(signal),
        enabled,
        staleTime: 10 * 60 * 1000,
        // 4xx blir ikke bedre av nye forsøk.
        retry: (antall, feil) =>
            !(feil instanceof GalleriApiError && feil.status < 500) && antall < 2,
        select,
    });
}

/** Elementene som kan velges i admin: sentralt galleri. */
export function useSentraltGalleri(enabled = true) {
    return useDibkGalleri(enabled, sentraleElementer);
}

/** Galleriet som oppslag på id (også kommunale DiBK-elementer), med status for lasting og feil. */
export function useSentraltGalleriTilstand(enabled = true): SentraltGalleriTilstand {
    const { data, isError } = useDibkGalleri<GalleriElement[]>(enabled);
    return useMemo<SentraltGalleriTilstand>(() => {
        if (data) return { status: 'ok', oppslag: lagSentraltGalleriOppslag(data) };
        return isError ? { status: 'feil' } : { status: 'laster' };
    }, [data, isError]);
}
